import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw, Settings } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function ConvertHeicToJpg({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [quality, setQuality] = useState([90]);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const SOURCE_FORMAT = "HEIC";
  const TARGET_FORMAT = "JPG";
  const ACCEPT_TYPES = ".heic,.heif,image/heic,image/heif";

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || `Convert ${SOURCE_FORMAT} to ${TARGET_FORMAT} - KayTool`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || `Free online ${SOURCE_FORMAT} to ${TARGET_FORMAT} converter. Convert iPhone HEIC photos to JPG instantly in your browser.`);
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/convert-heic-to-jpg`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        console.log('No CMS content for convert-heic-to-jpg page');
        updateSEOTags(null, `Convert ${SOURCE_FORMAT} to ${TARGET_FORMAT} - KayTool`);
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')
    );
    if (files.length > 0) {
      processMultipleImages(files);
    } else {
      toast.error(`Please drop ${SOURCE_FORMAT} files only`);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processMultipleImages(files);
    }
    e.target.value = '';
  };

  const getImageDimensions = (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  const convertImage = async (file) => {
    try {
      // Convert HEIC to JPEG using heic2any
      const blob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: quality[0] / 100
      });
      
      const resultBlob = Array.isArray(blob) ? blob[0] : blob;
      const convertedFile = new File(
        [resultBlob], 
        file.name.replace(/\.(heic|heif)$/i, '.jpg'), 
        { type: 'image/jpeg' }
      );
      return convertedFile;
    } catch (error) {
      console.error('HEIC conversion error:', error);
      throw new Error('Failed to convert HEIC file');
    }
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);

    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setCompressionProgress(Math.round(((i) / files.length) * 100));
      
      try {
        const converted = await convertImage(file);
        const dimensions = await getImageDimensions(converted);
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalFile: file,
          originalSize: file.size,
          dimensions: dimensions,
          convertedFile: converted,
          convertedSize: converted.size,
          preview: URL.createObjectURL(converted),
        });
      } catch (error) {
        console.error(`Error converting ${file.name}:`, error);
        toast.error(`Failed to convert ${file.name}`);
      }
    }

    setCompressionProgress(100);
    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    
    if (newImages.length > 0) {
      toast.success(`Converted ${newImages.length} image${newImages.length > 1 ? 's' : ''} to ${TARGET_FORMAT}!`);
    }
  };

  const removeImage = (id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter(i => i.id !== id);
    });
  };

  const downloadSingle = (image) => {
    saveAs(image.convertedFile, image.convertedFile.name);
  };

  const downloadAll = async () => {
    if (images.length === 0) return;
    
    if (images.length === 1) {
      downloadSingle(images[0]);
      return;
    }

    const zip = new JSZip();
    images.forEach((img) => {
      zip.file(img.convertedFile.name, img.convertedFile);
    });
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `heic-to-jpg-converted-${Date.now()}.zip`);
    toast.success("All images downloaded!");
  };

  const clearAll = () => {
    images.forEach(img => {
      URL.revokeObjectURL(img.preview);
    });
    setImages([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (embedded) {
    return (
      <div className="p-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium">Drop HEIC files here or click to upload</p>
          <p className="text-sm text-slate-500 mt-2">Convert iPhone photos to JPG format</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <QuickConvert />
            <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700">
                <Info className="w-3 h-3" />
                100% Local Processing
              </Badge>
              <Button variant="outline" size="icon" onClick={() => setDarkMode(prev => !prev)} className="rounded-full">
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
            HEIC to JPG
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Convert HEIC to JPG
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Convert iPhone HEIC photos to universally compatible JPG format. Fast, free, and processed entirely in your browser.
          </p>
        </div>

        {/* Upload Area */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                  : 'border-slate-300 dark:border-slate-600 hover:border-orange-400 dark:hover:border-orange-500'
              }`}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-xl font-medium text-slate-700 dark:text-slate-300">
                Drop HEIC/HEIF files here or click to upload
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Supports iPhone photos (.heic, .heif)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Quality Slider */}
        {images.length === 0 && (
          <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                <span className="font-medium text-slate-700 dark:text-slate-300">JPG Quality</span>
                <Badge variant="secondary">{quality[0]}%</Badge>
              </div>
              <Slider
                value={quality}
                onValueChange={setQuality}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-2">Higher quality = larger file size</p>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        {isProcessing && (
          <Card className="mb-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Converting...</span>
                <span className="text-sm text-slate-500">{compressionProgress}%</span>
              </div>
              <Progress value={compressionProgress} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {images.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Converted Images ({images.length})
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearAll} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Clear All
                </Button>
                <Button onClick={downloadAll} className="gap-2 bg-orange-600 hover:bg-orange-700">
                  <Download className="w-4 h-4" /> Download All
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden dark:bg-slate-800">
                  <div className="aspect-video bg-slate-100 dark:bg-slate-700 relative">
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-8 h-8"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-sm truncate mb-2 dark:text-white">{image.name}</p>
                    <div className="flex justify-between text-xs text-slate-500 mb-3">
                      <span>Original: {formatFileSize(image.originalSize)}</span>
                      <span>Converted: {formatFileSize(image.convertedSize)}</span>
                    </div>
                    {image.dimensions && (
                      <p className="text-xs text-slate-400 mb-3">
                        {image.dimensions.width} × {image.dimensions.height}
                      </p>
                    )}
                    <Button 
                      onClick={() => downloadSingle(image)} 
                      className="w-full gap-2 bg-orange-600 hover:bg-orange-700"
                    >
                      <Download className="w-4 h-4" /> Download JPG
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { title: "iPhone Compatible", desc: "Convert HEIC photos from iPhone to widely supported JPG format" },
            { title: "Quality Control", desc: "Adjust JPG quality to balance file size and image quality" },
            { title: "100% Private", desc: "All conversions happen in your browser - files never leave your device" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && (
        <div className="bg-white dark:bg-slate-800 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default ConvertHeicToJpg;
